// src/pages/AcceptedJobDetails.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db, storage } from '../firebase/config.js';
import { doc, getDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { format, parseISO } from 'date-fns';

const AcceptedJobDetails = (props) => {
  const { currentUser } = props;
  const { docId, period } = useParams();
  const [jobDetails, setJobDetails] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!docId || !period) {
      setError("Job details are missing.");
      setIsLoading(false);
      return;
    }

    const fetchJobDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const docRef = doc(db, 'sub_requests', docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          // --- MODIFIED AUTHORIZATION CHECK ---
          // Check if the current user is the one assigned to the job OR if they are an admin.
          const isAssignedSub = currentUser && data.filled && data.filled[period] &&
                                (data.filled[period].uid === currentUser.uid || data.filled[period].email === currentUser.email);
          
          const isAdmin = currentUser?.hasElevatedPrivileges;

          if (isAssignedSub || isAdmin) {
            
            let dateObj = null;
            if (data.date && typeof data.date === 'string') {
                try {
                    dateObj = parseISO(data.date);
                    if (isNaN(dateObj.getTime())) dateObj = null;
                } catch (e) { dateObj = null; }
            } else if (data.date && typeof data.date.toDate === 'function') {
                dateObj = data.date.toDate();
            }

            setJobDetails({
              id: docSnap.id,
              ...data,
              dateObj,
              periodNote: data.periodNotes?.[period] || "No specific notes for this period.",
              substituteInfo: data.filled[period] // Keep the sub info
            });

            // For file paths, use the tenantId from the document itself for consistency
            const tenantId = data.tenantId; 
            if (tenantId) {
              const basePath = `sub_files/${tenantId}/${docId}`; // Using tenantId from document
              const filesForPeriod = [];
              try {
                const folderRef = ref(storage, basePath);
                const result = await listAll(folderRef);
                const periodSpecificFilePromises = result.items
                  .filter(itemRef => itemRef.name.includes(`period_${period}_`))
                  .map(itemRef => getDownloadURL(itemRef).then(url => ({ name: itemRef.name, url })));
                
                const fetchedFiles = await Promise.all(periodSpecificFilePromises);
                setFiles(fetchedFiles);
              } catch (fileError) {
                if (fileError.code === 'storage/object-not-found') {
                  console.log(`No files folder or period-specific files for ${basePath}, period ${period}.`);
                } else {
                  console.error("Error fetching files:", fileError);
                }
                setFiles([]);
              }
            } else {
              console.warn("Could not fetch files because tenantId is missing from the sub_request document.");
              setFiles([]);
            }

          } else {
            setError("You are not authorized to view these job details, or the job is not assigned to you.");
            setJobDetails(null);
          }
        } else {
          setError("Sub request not found.");
        }
      } catch (err) {
        console.error("Error fetching job details:", err);
        setError("Failed to load job details.");
      }
      setIsLoading(false);
    };

    if (currentUser) {
        fetchJobDetails();
    } else {
        setIsLoading(false);
    }

  }, [docId, period, currentUser]);

  if (isLoading) {
    return (
      <div style={{ 
        maxWidth: '1000px', 
        margin: '20px auto', 
        padding: '20px',
        textAlign: 'center' 
      }}>
        <div style={{ 
          padding: '40px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          Loading job details...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        maxWidth: '1000px', 
        margin: '20px auto', 
        padding: '20px' 
      }}>
        <div style={{ 
          padding: '30px',
          backgroundColor: '#f8d7da',
          borderRadius: '8px',
          border: '1px solid #f5c6cb',
          textAlign: 'center',
          color: '#721c24'
        }}>
          <h3 style={{ margin: '0 0 15px 0' }}>Error</h3>
          <p style={{ margin: '0 0 20px 0' }}>{error}</p>
          <Link 
            to="/available-signups" 
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '10px 20px',
              textDecoration: 'none',
              borderRadius: '5px',
              display: 'inline-block'
            }}
          >
            Back to Available Signups
          </Link>
        </div>
      </div>
    );
  }

  if (!jobDetails) {
    return (
      <div style={{ 
        maxWidth: '1000px', 
        margin: '20px auto', 
        padding: '20px' 
      }}>
        <div style={{ 
          padding: '30px',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          border: '1px solid #ffeaa7',
          textAlign: 'center',
          color: '#856404'
        }}>
          <h3 style={{ margin: '0 0 15px 0' }}>No Job Details Found</h3>
          <Link 
            to="/available-signups"
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '10px 20px',
              textDecoration: 'none',
              borderRadius: '5px',
              display: 'inline-block'
            }}
          >
            Back to Available Signups
          </Link>
        </div>
      </div>
    );
  }

  const substituteName = jobDetails.substituteInfo?.name || "This job is not yet filled";
  const originalTeacherName = jobDetails.name;

  return (
    <div style={{ 
      maxWidth: '1000px', 
      margin: '20px auto', 
      padding: '20px' 
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h1 style={{ 
          fontSize: '2.5rem',
          fontWeight: 'bold',
          color: '#333',
          marginBottom: '10px'
        }}>
          Substitute Assignment Details
        </h1>
        <p style={{ 
          fontSize: '1rem',
          color: '#6c757d',
          fontStyle: 'italic',
          margin: 0
        }}>
          Details are automatically emailed to the selected teacher
        </p>
      </div>

      {/* Assignment Overview Card */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '25px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          fontSize: '1.5rem',
          color: '#007bff',
          marginBottom: '20px',
          borderBottom: '2px solid #007bff',
          paddingBottom: '10px'
        }}>
          Assignment Overview
        </h2>
        
        <div style={{ 
          backgroundColor: '#e3f2fd',
          padding: '20px',
          borderRadius: '6px',
          marginBottom: '20px',
          border: '1px solid #bbdefb'
        }}>
          <p style={{ 
            fontSize: '1.1rem',
            margin: '0 0 10px 0',
            color: '#1565c0'
          }}>
            This assignment for <strong>{originalTeacherName}</strong> is filled by:
          </p>
          <p style={{ 
            fontSize: '1.3rem',
            fontWeight: 'bold',
            margin: 0,
            color: '#0d47a1'
          }}>
            {substituteName}
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div>
            <strong style={{ color: '#495057' }}>Date:</strong>
            <div style={{ marginTop: '5px', fontSize: '1.1rem' }}>
              {jobDetails.dateObj ? format(jobDetails.dateObj, 'EEEE, MMMM d, yyyy') : jobDetails.date}
            </div>
          </div>
          <div>
            <strong style={{ color: '#495057' }}>Department:</strong>
            <div style={{ marginTop: '5px', fontSize: '1.1rem' }}>
              {jobDetails.department}
            </div>
          </div>
          <div>
            <strong style={{ color: '#495057' }}>Period:</strong>
            <div style={{ marginTop: '5px', fontSize: '1.1rem' }}>
              {period}
            </div>
          </div>
          <div>
            <strong style={{ color: '#495057' }}>Room:</strong>
            <div style={{ marginTop: '5px', fontSize: '1.1rem' }}>
              {jobDetails.periodRooms?.[period] || jobDetails.room || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '25px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          fontSize: '1.5rem',
          color: '#007bff',
          marginBottom: '20px',
          borderBottom: '2px solid #007bff',
          paddingBottom: '10px'
        }}>
          Notes for Period {period}
        </h2>
        <div style={{ 
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          whiteSpace: 'pre-wrap',
          fontSize: '1rem',
          lineHeight: '1.6',
          minHeight: '80px'
        }}>
          {jobDetails.periodNote}
        </div>
      </div>

      {/* Files Section */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '25px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          fontSize: '1.5rem',
          color: '#007bff',
          marginBottom: '20px',
          borderBottom: '2px solid #007bff',
          paddingBottom: '10px'
        }}>
          Attached Files for Period {period}
        </h2>
        
        {files.length > 0 ? (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '6px',
            border: '1px solid #e9ecef'
          }}>
            <ul style={{ 
              margin: 0,
              paddingLeft: '20px'
            }}>
              {files.map((file, idx) => {
                const decodedName = decodeURIComponent(file.name.split('%2F').pop());
                const displayFileName = decodedName.startsWith(`period_${period}_`) 
                                        ? decodedName.substring(`period_${period}_`.length) 
                                        : decodedName;
                return (
                  <li key={idx} style={{ marginBottom: '10px' }}>
                    <a 
                      href={file.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        color: '#007bff',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        padding: '8px 12px',
                        backgroundColor: 'white',
                        border: '1px solid #007bff',
                        borderRadius: '4px',
                        display: 'inline-block',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#007bff';
                        e.target.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.color = '#007bff';
                      }}
                    >
                      {displayFileName}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#fff3cd',
            padding: '20px',
            borderRadius: '6px',
            border: '1px solid #ffeaa7',
            color: '#856404',
            textAlign: 'center'
          }}>
            No files were attached for this specific period.
          </div>
        )}
      </div>

      {/* Navigation Section */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '25px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Link 
            to="/available-signups"
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              padding: '12px 24px',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
          >
            Back to Available Signups
          </Link>
          <Link 
            to="/my-roster"
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '12px 24px',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            View My Roster
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AcceptedJobDetails;